import * as React from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DataTableProps<T> {
	data: T[];
	columns: {
		id: string;
		header: string;
		accessorKey: keyof T;
		cell?: (item: T) => React.ReactNode;
		sortable?: boolean;
	}[];
	isLoading?: boolean;
	loadingContent?: React.ReactNode;
	emptyContent?: React.ReactNode;
	pageSize?: number;
	tableClassName?: string;
}

export function DataTable<T>({
	data,
	columns,
	isLoading = false,
	loadingContent,
	emptyContent,
	pageSize = 10,
	tableClassName,
}: DataTableProps<T>) {
	const [sorting, setSorting] = React.useState<{
		column: keyof T | null;
		direction: "asc" | "desc";
	}>({
		column: null,
		direction: "asc",
	});
	
	const [currentPage, setCurrentPage] = React.useState(1);
	const totalPages = Math.ceil(data.length / pageSize);

	// Sort the data
	const sortedData = React.useMemo(() => {
		if (!sorting.column) return data;

		return [...data].sort((a, b) => {
			const aValue = a[sorting.column];
			const bValue = b[sorting.column];

			if (aValue === bValue) return 0;
			
			if (aValue === null || aValue === undefined) return 1;
			if (bValue === null || bValue === undefined) return -1;
			
			const comparator = sorting.direction === "asc" ? 1 : -1;
			
			if (typeof aValue === "string" && typeof bValue === "string") {
				return aValue.localeCompare(bValue) * comparator;
			}
			
			if (aValue < bValue) return -1 * comparator;
			if (aValue > bValue) return 1 * comparator;
			
			return 0;
		});
	}, [data, sorting]);

	// Paginate the data
	const paginatedData = React.useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		const end = start + pageSize;
		return sortedData.slice(start, end);
	}, [sortedData, currentPage, pageSize]);

	// Sort handler
	const handleSort = (column: keyof T) => {
		setSorting((prevSorting) => {
			if (prevSorting.column === column) {
				return {
					column,
					direction: prevSorting.direction === "asc" ? "desc" : "asc",
				};
			}
			return {
				column,
				direction: "asc",
			};
		});
	};

	// Reset pagination when data changes
	React.useEffect(() => {
		setCurrentPage(1);
	}, [data.length]);

	const getSortIcon = (column: keyof T) => {
		if (sorting.column !== column) {
			return <ChevronsUpDown className="h-4 w-4 ml-1 text-muted-foreground/70" />;
		}

		return sorting.direction === "asc" ? (
			<ChevronUp className="h-4 w-4 ml-1" />
		) : (
			<ChevronDown className="h-4 w-4 ml-1" />
		);
	};

	return (
		<div className="space-y-4">

            {totalPages > 1 && (
				<div className="flex items-center justify-between">
					<div className="text-sm text-muted-foreground">
						Showing {paginatedData.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
						{Math.min(currentPage * pageSize, data.length)} of {data.length} entries
					</div>
					<div className="flex items-center space-x-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
							disabled={currentPage === 1}
						>
							Previous
						</Button>
						<div className="text-sm">{currentPage} / {totalPages}</div>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
							disabled={currentPage === totalPages}
						>
							Next
						</Button>
					</div>
				</div>
			)}

			<div className={cn("rounded-md border", tableClassName)}>
				<Table>
					<TableHeader>
						<TableRow>
							{columns.map((column) => (
								<TableHead
									key={column.id}
									className={cn(
										"whitespace-nowrap",
										column.sortable && "cursor-pointer hover:bg-muted/50"
									)}
									onClick={column.sortable ? () => handleSort(column.accessorKey) : undefined}
								>
									<div className="flex items-center">
										{column.header}
										{column.sortable && getSortIcon(column.accessorKey)}
									</div>
								</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={columns.length} className="text-center py-6">
									{loadingContent}
								</TableCell>
							</TableRow>
						) : paginatedData.length === 0 ? (
							<TableRow>
								<TableCell colSpan={columns.length} className="text-center py-6 text-muted-foreground">
									{emptyContent}
								</TableCell>
							</TableRow>
						) : (
							paginatedData.map((row, i) => (
								<TableRow key={i} className="transition-colors hover:bg-muted/50">
									{columns.map((column) => (
										<TableCell key={column.id} className="py-3">
											{column.cell ? column.cell(row) : String(row[column.accessorKey] || "")}
										</TableCell>
									))}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>

			{/* {totalPages > 1 && (
				<div className="flex items-center justify-between">
					<div className="text-sm text-muted-foreground">
						Showing {paginatedData.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
						{Math.min(currentPage * pageSize, data.length)} of {data.length} entries
					</div>
					<div className="flex items-center space-x-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
							disabled={currentPage === 1}
						>
							Previous
						</Button>
						<div className="text-sm">{currentPage} / {totalPages}</div>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
							disabled={currentPage === totalPages}
						>
							Next
						</Button>
					</div>
				</div>
			)} */}
		</div>
	);
}
